import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ProcessingState from '@/Models/ProcessingState';
import StepProps from '../StepProps';
import {
    ExperienceCriteriaForm,
    experienceCriteriaFormSchema,
    mapRhfCriteriaToExperienceCriteria,
    mapExperienceCriteriaToRhfCriteria,
} from './Components/ExperienceCriteriaForm';
import IdentifyExperienceStepFields from './Components/IdentifyExperienceStepFields/IdentifyExperienceStepFields';
import IdentifyExperienceStepHeader from './Components/IdentifyExperienceStepHeader';
import IdentifyExperienceStepNavigation from './Components/IdentifyExperienceStepNavigation';
import { ExperienceCriteria } from '@/Models/ExperienceCriteria';

interface IdentifyExperienceStepProps extends Omit<StepProps, 'handleNext'> {
    processingState: ProcessingState;
    experienceCriteriaProcessingState: ProcessingState;
    experienceCriteria: Array<ExperienceCriteria>;
    setExperienceCriteria: (valueSet: { experienceCriteria: Array<ExperienceCriteria> }) => void;
    resetExperienceCriteriaProcessingState: () => void;
    onSubmit: (onValid: ExperienceCriteriaForm) => Promise<void>;
    isEditMode: boolean;
}

const IdentifyExperienceStep = ({
    activeStep,
    handleBack,
    processingState,
    experienceCriteriaProcessingState,
    experienceCriteria,
    setExperienceCriteria,
    resetExperienceCriteriaProcessingState,
    onSubmit,
    isEditMode,
}: IdentifyExperienceStepProps): React.JSX.Element => {
    const form = useForm<ExperienceCriteriaForm>({
        mode: 'all',
        defaultValues: {
            criteria: experienceCriteria.map(mapExperienceCriteriaToRhfCriteria),
        },
        resolver: yupResolver(experienceCriteriaFormSchema),
    });

    const { reset, handleSubmit } = form;

    useEffect(() => {
        if (experienceCriteriaProcessingState === ProcessingState.Success) {
            reset({
                criteria: experienceCriteria.map(mapExperienceCriteriaToRhfCriteria),
            });
            resetExperienceCriteriaProcessingState();
        }
    }, [experienceCriteriaProcessingState, reset, resetExperienceCriteriaProcessingState]);

    const handleBackWrapper = () => {
        if (handleBack) {
            const { criteria } = form.getValues();

            setExperienceCriteria({
                experienceCriteria: criteria.map(mapRhfCriteriaToExperienceCriteria),
            });
            handleBack();
        }
    };

    return (
        <div className="mx-auto w-full max-w-4xl self-center">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <IdentifyExperienceStepHeader />
                    <IdentifyExperienceStepFields
                        experienceCriteriaProcessingState={experienceCriteriaProcessingState}
                        isEditMode={isEditMode}
                    />
                    <IdentifyExperienceStepNavigation
                        activeStep={activeStep}
                        handleBack={handleBackWrapper}
                        processingState={processingState}
                        experienceCriteriaProcessingState={experienceCriteriaProcessingState}
                        isEditMode={isEditMode}
                    />
                </form>
            </FormProvider>
        </div>
    );
};

export default IdentifyExperienceStep;
